/**
 * Block 10 (2026-05-27): Reusable widget wrapper.
 *
 * **First widget pattern у проекті.** Закладає інфраструктуру для майбутніх
 * dashboard widgets (P&L summary, cash flow, recent docs тощо).
 *
 * Props:
 * - title: заголовок widget.
 * - icon: опціональна іконка/emoji перед title.
 * - actions: header-right actions (toggles, refresh button etc).
 * - loading/error/isEmpty з emptyMessage — статичні UI states.
 * - children: основний контент.
 */
import type { ReactNode } from 'react';

interface Props {
  title: string;
  icon?: ReactNode;
  actions?: ReactNode;
  loading?: boolean;
  error?: string | null;
  isEmpty?: boolean;
  emptyMessage?: string;
  children: ReactNode;
}

export default function DashboardWidget({
  title,
  icon,
  actions,
  loading,
  error,
  isEmpty,
  emptyMessage,
  children,
}: Props) {
  return (
    <div
      data-testid="dashboard-widget"
      className="card"
      style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          borderBottom: '1px solid #e2e8f0',
          paddingBottom: '0.5rem',
        }}
      >
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          {icon && <span aria-hidden>{icon}</span>}
          {title}
        </h3>
        {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
      </div>

      <div style={{ minHeight: 80 }}>
        {loading && (
          <p style={{ color: '#64748b', fontStyle: 'italic' }}>Завантаження…</p>
        )}
        {!loading && error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        {!loading && !error && isEmpty && (
          <p style={{ color: '#64748b' }}>{emptyMessage ?? 'Немає даних'}</p>
        )}
        {!loading && !error && !isEmpty && children}
      </div>
    </div>
  );
}
